import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Bus, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const go = (role) => {
    const target = role === "admin" ? "/admin" : role === "driver" ? "/driver" : "/parent";
    nav(loc.state?.from?.pathname || target, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bienvenue " + (res.user.name || ""));
      go(res.user.role);
    } else {
      toast.error(res.error);
    }
  };

  const quick = async (e_, em, pw) => {
    setEmail(em); setPassword(pw);
    setLoading(true);
    const res = await login(em, pw);
    setLoading(false);
    if (res.ok) { toast.success("Bienvenue " + res.user.name); go(res.user.role); }
    else toast.error(res.error);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="p-8 lg:p-16 flex flex-col">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="login-logo">
          <div className="w-8 h-8 bg-[#FFC60A] border border-zinc-900 flex items-center justify-center">
            <Bus size={18} />
          </div>
          <span className="font-display font-black text-xl tracking-tighter">LOGIBUS</span>
        </Link>

        <div className="max-w-sm">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Connexion</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Bon retour.</h1>
          <p className="text-sm text-zinc-600 mt-2">Accédez à votre tableau de bord LOGIBUS.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-zinc-900 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white"
                placeholder="vous@ecole.fr"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Mot de passe</label>
              <div className="relative mt-1">
                <input
                  data-testid="login-password-input"
                  type={showPwd ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-zinc-900 px-3 py-3 pr-11 focus:outline-none focus:ring-2 focus:ring-zinc-900 bg-white"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="login-password-toggle"
                  onClick={() => setShowPwd((v) => !v)}
                  title={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-0 top-0 h-full px-3 text-zinc-500 hover:text-zinc-900"
                >
                  {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>
            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#FFC60A] border border-zinc-900 py-3 font-bold hover:bg-[#ffd43a] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <>Se connecter <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-8 border border-zinc-900 p-4">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-2">Comptes démo</div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="demo-admin-btn" onClick={(e) => quick(e, "admin@shuttletrack.com", "Admin@2026")} className="text-xs font-semibold border border-zinc-900 py-2 hover:bg-zinc-900 hover:text-white">Admin</button>
              <button data-testid="demo-driver-btn" onClick={(e) => quick(e, "driver@shuttletrack.com", "Driver@2026")} className="text-xs font-semibold border border-zinc-900 py-2 hover:bg-zinc-900 hover:text-white">Chauffeur</button>
              <button data-testid="demo-parent-btn" onClick={(e) => quick(e, "parent@shuttletrack.com", "Parent@2026")} className="text-xs font-semibold border border-zinc-900 py-2 hover:bg-zinc-900 hover:text-white">Parent</button>
            </div>
          </div>

          <p className="mt-6 text-sm text-zinc-600">
            Pas encore de compte ? <Link data-testid="login-to-register" to="/register" className="font-bold underline">Créer un compte</Link>
          </p>
        </div>
      </div>

      <div className="hidden md:block relative border-l border-zinc-900 bg-zinc-900">
        <img src="https://images.unsplash.com/photo-1754573458272-e24d7ad5d2c6?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85" alt="bus" className="w-full h-full object-cover opacity-85" />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-900/80 via-transparent to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#FFC60A]">Live • Now</div>
          <p className="font-display font-black text-4xl tracking-tighter mt-2">"Nos tournées sont 30% plus rapides."</p>
          <p className="text-xs text-zinc-300 mt-2">— Elena, coordinatrice École Montaigne</p>
        </div>
      </div>
    </div>
  );
}
